import * as React from 'react';
import { connect } from 'react-redux';
import { apiRequest } from '../../APIClient';
import { APIError } from '../../APIError';
import { sensorFilter } from '../../constants';
import { dashboardSelector, SensorMetricsMap } from '../../reducers/dashboard';
import { sessionSelector } from '../../reducers/session';
import { Dispatch, SensorEntry } from '../../types';
import LoadingIndicator from '../../widgets/LoadingIndicator';
import Panel from '../../widgets/Panel';
import ViewHeader from '../../widgets/ViewHeader';
import Viewport from '../../widgets/Viewport';
import './Dashboard.scss';
import SensorMetricList from './SensorMetricList';
import { PageTitle } from '../../widgets/PageTitle';

export type SensorWithMetrics = SensorEntry & {
  obs: Array<{ x: number; y: number }>;
};

type OwnProps = {};

export type StoreProps = {
  sensors: Array<SensorEntry>;
  sensorMetrics: SensorMetricsMap;
  isExpiredSensors: boolean;
  isLoading: boolean;
  error: APIError | null;
};

export type DispatchProps = {
  fetchSensorMetrics: () => void;
};

export type Props = OwnProps & StoreProps & DispatchProps;

type State = {
  physicalSensors: Array<SensorWithMetrics>;
  vlcSensors: Array<SensorWithMetrics>;
  cloudSensors: Array<SensorWithMetrics>;
  scanners: Array<SensorWithMetrics>;
};

const getHealthSortOrder = (status: string) => {
  switch (status) {
    case 'DOWN':
      // show first
      return 1;
    case 'OK':
      // show last
      return 10;
    default:
      return 5;
  }
};

/**
 * Comparator for sensor objects, comparing the health and then name
 * @param {SensorEntry} sensor1 - sensor to compare
 * @param {SensorEntry} sensor2 - sensor to compare
 * @return {number} integer representing relative difference between sensors
 */
const sensorComparator = (
  sensor1: SensorEntry,
  sensor2: SensorEntry,
): number => {
  const health1 = getHealthSortOrder(sensor1.health);
  const health2 = getHealthSortOrder(sensor2.health);

  if (health1 === health2) {
    return (sensor1.shorthand || sensor1.id).localeCompare(
      sensor2.shorthand || sensor2.id,
    );
  } else {
    return health1 - health2;
  }
};

const bucketSensors = (
  sensors: Array<SensorEntry>,
  sensorMetrics: SensorMetricsMap,
): State => {
  const sensorsWithHealthAndMetrics: Array<SensorWithMetrics> = sensors
    .filter(sensorFilter.hasHealth)
    .map((sensor) => ({ ...sensor, obs: sensorMetrics[sensor.id] }))
    .sort(sensorComparator);
  return {
    cloudSensors: sensorsWithHealthAndMetrics.filter(sensorFilter.isCloud),
    vlcSensors: sensorsWithHealthAndMetrics.filter(sensorFilter.isVlc),
    physicalSensors: sensorsWithHealthAndMetrics.filter(
      sensorFilter.isPhysical,
    ),
    scanners: sensorsWithHealthAndMetrics.filter(sensorFilter.isScanner),
  };
};

const SensorDetailsView = (props: Props) => {
  const {
    sensors,
    sensorMetrics,
    error,
    isLoading,
    isExpiredSensors,
    fetchSensorMetrics,
  } = props;
  React.useEffect(() => {
    if (isExpiredSensors) {
      fetchSensorMetrics();
    }
  }, [isExpiredSensors, fetchSensorMetrics]);
  const { cloudSensors, physicalSensors, vlcSensors, scanners } = React.useMemo(
    () => bucketSensors(sensors, sensorMetrics),
    [sensors, sensorMetrics],
  );

  return (
    <Viewport>
      <PageTitle title="Sensor & Scanner Status" />
      <ViewHeader title="Sensor & Scanner Status" />
      {isLoading ? (
        <Panel title="Sensors" className="min-height-for-loading-indicator">
          <LoadingIndicator size="lg" centered>
            Loading sensor details...
          </LoadingIndicator>
        </Panel>
      ) : physicalSensors.length + scanners.length + cloudSensors.length ===
        0 ? (
        <Panel>
          <div className="text-center">
            There are no sensors or scanners installed.
          </div>
        </Panel>
      ) : (
        <div>
          {physicalSensors.length > 0 && (
            <SensorMetricList
              showStatus={true}
              title="Physical Sensors"
              sensors={physicalSensors}
              error={error}
            />
          )}
          {vlcSensors.length > 0 && (
            <SensorMetricList
              showStatus={true}
              title="Virtual Sensors"
              sensors={vlcSensors}
              error={error}
            />
          )}
          {scanners.length > 0 && (
            <SensorMetricList
              showStatus={false}
              title="Risk Scanners"
              sensors={scanners}
              error={error}
            />
          )}
          {cloudSensors.length > 0 && (
            <SensorMetricList
              showStatus={false}
              title="Cloud Sensors"
              sensors={cloudSensors}
              error={error}
            />
          )}
        </div>
      )}
    </Viewport>
  );
};

export default connect<StoreProps, DispatchProps, OwnProps>(
  (store): StoreProps => ({
    sensors: sessionSelector.getSensors(store),
    sensorMetrics: dashboardSelector.getSensorMetrics(store),
    isExpiredSensors: dashboardSelector.isExpiredSensors(store),
    isLoading: dashboardSelector.isLoadingSensors(store),
    error: dashboardSelector.getSensorMetricsError(store),
  }),
  (dispatch: Dispatch): DispatchProps => ({
    fetchSensorMetrics: () => dispatch(apiRequest.getSensorMetrics()),
  }),
)(SensorDetailsView);
